import {
    ArrowRightIcon,
    Avatar,
    Button,
    CheckmarkCircleIcon,
    CloseIcon,
    Divider,
    Flex,
    Image,
    Loader,
    Skeleton,
    Text,
    Tooltip,
} from "@fluentui/react-northstar";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    CalendarEdit16Regular,
    CommentMultipleFilled,
    TimerRegular,
    WarningFilled,
} from "@fluentui/react-icons";
import { useMutation } from "react-query";
import { _api_approve_or_edit_slot } from "../../api";
import EditedSlots from "./EditedSlots";

export default function CellDetail({
    setShowEditDialog,
    showEditDialog,
    slot_data,
    refetchSlotData,
    isLoadingSlotData,
}) {
    const [configToast, setConfigToast] = useState({
        visible: false,
        content: "",
        level: "",
    });
    const [showButtons, setShowButtons] = useState(true);

    const { mutate, isLoading: isLoadingEditShift } = useMutation(
        "approove_refuse_edited_slot",
        (data) => {
            return (
                _api_approve_or_edit_slot(data),
                {
                    onSuccess: () => {
                        refetchSlotData();
                    },
                    onError: (error) => {
                        console.error("Erreur lors de la mise à jour :", error);
                    },
                }
            );
        }
    );

    const ApproveEditSlot = (step) => {
        mutate(
            {
                user_id: slot_data?.user?.id,
                shift_id: slot_data.id,
                step,
            },
            {
                onSuccess: (res) => {
                    if (res.success) {
                        setConfigToast({
                            visible: true,
                            content: `Crénaux ${step}`,
                            level: "success",
                        });
                        setShowButtons(false);
                    } else {
                        setConfigToast({
                            visible: true,
                            content: `Crénaux ${step}`,
                            level: "danger",
                        });
                    }
                },
            }
        );
        return;
    };

    useEffect(() => {
        if (configToast.visible) {
            const timer = setTimeout(() => {
                setConfigToast((prev) => ({ ...prev, visible: false }));
            }, 3000);

            return () => clearTimeout(timer); // Nettoie le timer si le composant est démonté
        }
    }, [configToast.visible]);

    // if (isLoadingSlotData) return <Loader label="Chargement slot data " />;

    return (
        <Flex column>
            {/* name & avatar */}
            <Flex gap="gap.small" vAlign="center">
                {isLoadingSlotData ? (
                    <Flex vAlign="center" gap="gap.small">
                        <Skeleton animation="wave">
                            <Skeleton.Shape round width="32px" height="32px" />
                        </Skeleton>
                        <Skeleton animation="wave">
                            <Skeleton.Line width="100px" />
                        </Skeleton>
                    </Flex>
                ) : (
                    <>
                        <Avatar
                            name={slot_data?.user?.name}
                            image={slot_data?.user?.avatar}
                        />
                        <UserNameText content={slot_data?.user?.name} />
                    </>
                )}
            </Flex>

            <MainContent column fill>
                {/* tooltip icons  */}
                <TooltipFlex fill hAlign="center" gap="gap.small">
                    {/* badgeuse modifié tooltip  */}
                    {slot_data?.exist_edited_shifts_from_badge && (
                        <Tooltip
                            content="Timbrage modifié "
                            pointing
                            subtle={false}
                            trigger={
                                <BgIcon bgColor={"#FBF6D9"} color={"#FFAA44"}>
                                    <TimerRegular />
                                </BgIcon>
                            }
                            unstable_disableTether="all"
                        />
                    )}

                    {/* Pause respected tooltip */}
                    {isLoadingSlotData ? (
                        <Flex>
                            <Skeleton fill hAlign="center" animation="pulse">
                                <Skeleton.Line width="30px" height="30px" />
                            </Skeleton>
                        </Flex>
                    ) : (
                        !slot_data?.pause_respected && (
                            <Tooltip
                                content="Temps de pause non respecté"
                                pointing
                                subtle={false}
                                trigger={
                                    <BgIcon
                                        bgColor={"#FCF4F6"}
                                        color={"#C4314B"}
                                    >
                                        <WarningFilled color="red" />
                                    </BgIcon>
                                }
                                unstable_disableTether="all"
                            />
                        )
                    )}

                    {/* commentaire tooltip  */}
                    {slot_data?.comment && (
                        <Tooltip
                            content="Commentaire(s)"
                            pointing
                            subtle={false}
                            trigger={
                                <BgIcon bgColor={"#E8EBFA"} color={"#424242"}>
                                    <CommentMultipleFilled />
                                </BgIcon>
                            }
                            unstable_disableTether="all"
                        />
                    )}
                </TooltipFlex>
                <Flex fill hAlign="center">
                    {isLoadingSlotData ? (
                        <Flex hAlign="center">
                            <Skeleton animation="pulse">
                                <Skeleton.Line width="80px" height="30px" />
                            </Skeleton>
                        </Flex>
                    ) : (
                        <TotalHoursText
                            content={
                                <p>
                                    {slot_data?.hours_count} <sub>heures</sub>
                                </p>
                            }
                        />
                    )}
                </Flex>
                <Divider size={1} styles={{ color: "#d1d1d1" }} />
                <Flex gap="gap.smaller" column>
                    <Label content="Commentaire" />
                    <CommentText
                        content={
                            slot_data?.comment !== null &&
                            slot_data?.comment?.length !== 0
                                ? slot_data?.comment
                                : "Non renseigné "
                        }
                    />
                </Flex>
                <SlotFlex vAlign="center" space="between">
                    <Flex gap="gap.small" vAlign="center">
                        <SlotText content="Créneaux" />
                        <Flex
                            styles={{
                                padding: "2px 4px",
                                borderRadius: "4px",
                                cursor: "pointer",
                            }}
                        >
                            <CalendarEdit16Regular
                                opacity={
                                    slot_data?.user_can_edit_shifts ? 1 : 0.5
                                }
                                onClick={() => {
                                    slot_data?.user_can_edit_shifts &&
                                        setShowEditDialog(!showEditDialog);
                                }}
                            />
                        </Flex>
                    </Flex>
                    {configToast.visible && (
                        <TaostFlex vAlign="center" gap="gap.small">
                            <CheckmarkCircleIcon />
                            <Text content={configToast.content} />
                            <CloseIcon
                                styles={{ color: "#212121" }}
                                onClick={() =>
                                    setConfigToast({
                                        visible: false,
                                        content: "",
                                        level: "",
                                    })
                                }
                            />
                        </TaostFlex>
                    )}
                </SlotFlex>
                <Flex gap="gap.small" column fill>
                    <Flex
                        vAlign="center"
                        fill
                        gap="gap.small"
                        space="between"
                        // hAlign="center"
                        column
                    >
                        {!slot_data ? (
                            <Loader label="Chargement des données" />
                        ) : (
                            <EditedSlots
                                refetchSlotData={refetchSlotData}
                                setConfigToast={setConfigToast}
                                slot_data={slot_data}
                                ApproveEditSlot={ApproveEditSlot}
                                setShowEditDialog={setShowEditDialog}
                                showEditDialog={showEditDialog}
                            />
                        )}
                    </Flex>
                    {/* action  */}
                    {/* activer et refuser buttons  */}
                </Flex>
            </MainContent>
        </Flex>
    );
}

const TaostFlex = styled(Flex)`
    background: #e7f2da;
    border: 1px solid #bdda9b;
    color: #237b4b;
    padding: 6px 16px;
    height: 32px;
    font-size: 14px;
`;

const UserNameText = styled(Text)`
    color: #242424;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
`;

const BgIcon = styled(Flex)`
    padding: 8px 8px;
    gap: 8px;
    align-self: stretch;
    background: ${({ bgColor }) => bgColor};
    color: ${({ color }) => color};
    border-radius: 50%;
`;

const MainContent = styled(Flex)`
    padding: 16px 10px;
    gap: 8px;
    /* border-bottom: 1px solid #d1d1d1; */
`;

const TotalHoursText = styled(Text)`
    color: #242424;
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px;
`;

const TooltipFlex = styled(Flex)`
    padding-top: 4px;
`;

const Label = styled(Text)`
    color: #616161;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
`;

const CommentText = styled(Text)`
    color: #616161;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    margin-left: 12px;
`;

const SlotText = styled(Text)`
    color: #242424;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
`;

const SlotFlex = styled(Flex)`
    margin-top: 16px;
    height: 40px;
`;
